import type { Api } from "./Api.js";
import type { LiveStore, Topic } from "./LiveStore.js";
import { h } from "./dom.js";

export interface AppContext {
  api: Api;
  live: LiveStore;
  toast(message: string, kind?: "info" | "error"): void;
  navigate(route: string): void;
}

/** Base class for a dashboard page: owns its root element and its live subscriptions. */
export abstract class Page {
  readonly el: HTMLElement = h("section", { class: "page" });
  private cleanups: Array<() => void> = [];

  constructor(protected readonly ctx: AppContext) {}

  abstract readonly title: string;
  abstract mount(): void | Promise<void>;

  unmount(): void {
    for (const fn of this.cleanups) fn();
    this.cleanups = [];
  }

  protected listen(topic: Topic, fn: () => void): void {
    this.cleanups.push(this.ctx.live.on(topic, fn));
  }

  protected every(ms: number, fn: () => void): void {
    const id = window.setInterval(fn, ms);
    this.cleanups.push(() => window.clearInterval(id));
  }

  /** Runs an action and shows its error as a toast instead of throwing. */
  protected async attempt<T>(fn: () => Promise<T>, success?: string): Promise<T | undefined> {
    try {
      const result = await fn();
      if (success) this.ctx.toast(success);
      return result;
    } catch (err) {
      this.ctx.toast((err as Error).message, "error");
      return undefined;
    }
  }
}
